<script lang="ts" setup>
/**
 * Persistent "Following AI agent" banner. Mounted once at the App.vue
 * root so it survives every route change and layout swap. Shares state
 * with the per-page AiPresenceWidget via useAiPresence().
 */

import { computed } from "vue"
import { useAiPresence } from "@/composables/useAiPresence"

const {
    isFollowing,
    followedSession,
    followedSessionActive,
    followingSessionId,
    stopFollowing,
} = useAiPresence()

const shortId = computed(() => {
    const id = followingSessionId.value
    if (!id) return ""
    return id.length > 8 ? id.slice(0, 8) : id
})
</script>

<template lang="pug">
.follow-banner(v-if="isFollowing", :class="{ 'is-stale': !followedSessionActive }")
    i.fas.fa-robot
    span.banner-text(v-if="followedSessionActive")
        | Following AI agent #[code {{ shortId }}]
        template(v-if="followedSession?.activity?.label") &nbsp;— {{ followedSession.activity.label }}
    span.banner-text(v-else)
        | AI agent #[code {{ shortId }}] disconnected
    button.banner-stop(type="button", @click="stopFollowing", title="Stop following")
        i.fas.fa-times
</template>

<style lang="css" scoped>
.follow-banner {
    position: fixed;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    z-index: 2000;
    background: rgb(242, 116, 5);
    color: white;
    padding: 6px 12px 6px 14px;
    border-radius: 0 0 8px 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    font-weight: 500;
}

.follow-banner code {
    background: rgba(255, 255, 255, 0.2);
    padding: 1px 5px;
    border-radius: 3px;
    font-size: 11px;
    color: white;
}

.follow-banner.is-stale {
    background: var(--mid-grey, #888);
}

.follow-banner .banner-stop {
    background: rgba(0, 0, 0, 0.1);
    border: none;
    border-radius: 4px;
    color: white;
    padding: 3px 7px;
    cursor: pointer;
    transition: background 0.15s ease;
}

.follow-banner .banner-stop:hover {
    background: rgba(0, 0, 0, 0.25);
}
</style>
